import type { SorteDeLien } from '@nextilt/commun';
import type { Priorite } from '../api/types';
import { MOTS_PRIORITE } from './etats';

/**
 * Les mots de la Carte et de la Hiérarchie : le calque, le filtre, l'avancement au centre, et
 * les gestes d'une bulle.
 */

/**
 * Le calque de la carte et de la Hiérarchie (ticket 04 du chantier `priorites`), choisi dans
 * ⚙ : une importance mise en avant, le reste s'efface.
 */
export const METTRE_EN_AVANT = 'Mettre en avant';
export const AUCUNE_EN_AVANT = 'Rien en avant';
export function motDuCalque(priorite: Priorite): string {
  return `${MOTS_PRIORITE[priorite]} en avant, le reste s’efface`;
}
/** L'étiquette d'une étape allumée par le calque : combien elle porte de cette priorité. */
export function etiquetteDuCalque(priorite: Priorite, combien: number): string {
  return `${MOTS_PRIORITE[priorite]} · ${combien}`;
}
export function titreDuCalque(priorite: Priorite, combien: number): string {
  return `${combien} ${combien > 1 ? 'bulles' : 'bulle'} « ${MOTS_PRIORITE[priorite]} » dedans`;
}

/*
 * La Carte refaite d'après sa maquette (chantier `revue-design`, ticket 04, `design/carte.html`,
 * décisions d'Igor du 2026-10-03, « La Carte » 1 à 5) : le filtre en pastilles et sa phrase,
 * « ? » à la place de la légende, l'avancement au centre. Depuis le chantier `tete-du-projet`
 * (modification 02), la légende est dans l'ampoule de l'Aide, sous `AIDE_DE_LA_CARTE`.
 */
/** La première pastille du filtre : rien de mis en avant. */
export const FILTRE_TOUT = 'Tout';
export const FILTRER_LA_CARTE = 'Mettre une importance en avant';
const BULLES_DE: Record<Priorite, [string, string]> = {
  urgent: ['bulle urgente', 'bulles urgentes'],
  important: ['bulle importante', 'bulles importantes'],
  plus_tard: ['bulle « quand tu veux »', 'bulles « quand tu veux »'],
};
/**
 * La phrase du filtre : « 2 bulles urgentes sur 119, dans 2 branches ». `total` : ce que la
 * feuille contient, `null` si le compte serait partiel (la phrase se tait alors sur le total).
 */
export function phraseDuFiltre(
  priorite: Priorite,
  combien: number,
  total: number | null,
  branches: number
): string {
  const [une, plusieurs] = BULLES_DE[priorite];
  const sur = total === null ? '' : ` sur ${total}`;
  if (combien === 0) return `Aucune ${une}${sur}`;
  return `${combien} ${combien > 1 ? plusieurs : une}${sur}, dans ${branches} ${branches > 1 ? 'branches' : 'branche'}`;
}
/** L'avancement, sous le titre du centre de la carte : « 4 sur 119 faites ». */
export function faitesSurN(faites: number, total: number): string {
  return `${faites} sur ${total} faites`;
}
/** Le titre de la légende de la carte, dans l'ampoule de l'Aide (chantier `tete-du-projet`, 02). */
export const AIDE_DE_LA_CARTE = 'Lire la carte';
export const RECENTRER = 'Recentrer';
export const RECENTRER_TITRE = 'Le titre au milieu, à 100 %';
export const PROFONDEUR = 'Profondeur';

/**
 * Les deux gestes d'une bulle, partout (retour 3 des testeurs, 2026-09-26) : **un clic entre
 * dedans, un double-clic ouvre sa fiche**. Carte, Liste, Hiérarchie, « Projets » les disent
 * avec ces mots-là, et le tuto avec les siens (« Un clic entre dans une bulle, un double-clic
 * ouvre sa fiche »). La bulle du centre, où l'on est déjà, n'a que le second.
 */
export const DEUX_GESTES = 'Clic : entrer dedans · double-clic : sa fiche';
export const GESTE_DU_CENTRE = 'Double-clic : sa fiche';
/** Le titre d'une feuille au téléphone (chantier `telephone-cadre`, ticket 04) : un toucher. */
export const TITRE_AU_POUCE = 'Toucher : sa fiche';

/*
 * Les liens de la carte (chantier liens-entre-bulles, ticket 03, spec du chantier, « Les mots »,
 * validés par Igor le 2026-10-24) : l'étiquette d'un lien vers une bulle hors de la feuille, selon
 * sa sorte et le côté d'où on la lit ; les trois signes d'un trait cliqué et leurs confirmations.
 */
/**
 * Le début de l'étiquette : « Attend « X » » du côté de la bulle qui attend, « Retient « X » » de
 * l'autre (l'ancien « Sert aussi à ») ; « Sert à » et « S'appuie sur » ; « Touche à » des deux.
 * `sort` : la première bulle du lien est sur la feuille ; `entre` : c'est l'autre.
 */
export function debutDeLEtiquette(sorte: SorteDeLien, sens: 'sort' | 'entre'): string {
  if (sorte === 'touche_a') return 'Touche à « ';
  if (sorte === 'sert_a') return sens === 'sort' ? 'Sert à « ' : 'S’appuie sur « ';
  return sens === 'sort' ? 'Retient « ' : 'Attend « ';
}
/** Le nom du groupe des trois signes, pour un lecteur d'écran. */
export const CE_LIEN = 'Ce lien';
export const CACHER_LE_LIEN = 'Cacher ce lien';
export const MONTRER_LE_LIEN = 'Montrer ce lien';
export const INVERSER_LE_LIEN = 'Inverser ce lien';
export const SUPPRIMER_LE_LIEN = 'Supprimer ce lien';
export const INVERSER_CE_LIEN_QUESTION = 'Inverser ce lien ?';
export const SUPPRIMER_CE_LIEN_QUESTION = 'Supprimer ce lien ?';
export const INVERSER = 'Inverser';
/** Un « attend » qui, retourné, fermerait une boucle : rien ne change. */
export const INVERSE_FERMERAIT_UNE_BOUCLE = 'Retourné, ce lien fermerait une boucle : rien n’a changé.';
export const LIEN_PAS_CHANGE = 'Le lien n’a pas pu changer. Réessaie.';
